/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import { memo } from 'react';
import type { KeyboardEvent } from 'react';

import Highlighter, { Chunk, FindChunks } from 'react-highlight-words';

import { formatNumberToPhone } from '@components/common/PhoneMask/formatNumberToPhone';
import { KeyboardKeyCodeEnum } from '@modules/app/appConstants';
import type { ISuperSearchTranslation } from '@modules/internationalization/types/corporate';

import type { ISuperSearchMobile } from '../../superSearchTypes';
import {
    SuperSearchListGroupStyled,
    SuperSearchListGroupTitleStyled,
    SuperSearchListItem,
    SuperSearchListItemSecondary,
    SuperSearchListItemTitle,
} from '../styled/SuperSearchListStyled';

/**
 * @description Super search item mobile props
 * @interface ISuperSearchItemMobilesProps
 * @property {ISuperSearchMobile[]} mobiles - mobiles list
 * @property {string} searchValue - search value
 * @property {(payload: ISetFilterPayload) => void} selectItem - select callback
 * @property {ICommonTranslation} translation - current locale
 */
export interface ISuperSearchItemMobilesProps {
    mobiles: ISuperSearchMobile[];
    searchValue: string;
    selectItem: (value: string) => void;
    translation: ISuperSearchTranslation;
}

export const correctHighlightIndex = (start: number, end: number) => {
    const symbolsPos = [2, 5, 7];
    let startOffset = 3;
    let endOffset = 3;
    for (const num of symbolsPos) {
        if (start > num) {
            startOffset++;
        }
        if (end - 1 > num) {
            endOffset++;
        }
    }
    return {
        start: start + startOffset,
        end: end + endOffset,
    };
};

export const findChunksOfWords = (
    {
        searchWords,
        textToHighlight,
    }: FindChunks) => {
    const chunks: Chunk[] = [];

    const formatNumber = textToHighlight.substring(3).replace(/[^0-9]/g, '');

    searchWords.forEach((word: string | RegExp) => {
        const start = formatNumber.indexOf(word.toString());
        if (start < 0) {
            return;
        }
        const end = start + word.toString().length;
        chunks.push(correctHighlightIndex(start, end));
    });
    return chunks;
};

const SuperSearchItemMobiles = (props: ISuperSearchItemMobilesProps) => {
    const selectHandler = (item: ISuperSearchMobile) => {
        props.selectItem(item.id);
    };
    const keyDownHandler = (event: KeyboardEvent, item: ISuperSearchMobile) => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER) {
            selectHandler(item);
        }
    };

    return (
        <SuperSearchListGroupStyled>
            <SuperSearchListGroupTitleStyled>
                {props.translation.mobiles}
            </SuperSearchListGroupTitleStyled>
            {
                props.mobiles.map((item: ISuperSearchMobile) => {
                    const isPhoneTitle = item.msisdn.includes(props.searchValue);
                    return (
                        <SuperSearchListItem
                            key={item.id + item.msisdn}
                            onClick={() => selectHandler(item)}
                            onKeyDown={(e: KeyboardEvent) => keyDownHandler(e, item)}
                        >
                            <SuperSearchListItemTitle>
                                <Highlighter
                                    autoEscape={true}
                                    findChunks={isPhoneTitle ? findChunksOfWords : undefined}
                                    highlightClassName="super-search__item-text_bold"
                                    searchWords={props.searchValue.split(' ')}
                                    textToHighlight={isPhoneTitle ? formatNumberToPhone(item.msisdn) : item.name}
                                />
                            </SuperSearchListItemTitle>
                            <SuperSearchListItemSecondary>
                                <Highlighter
                                    autoEscape={true}
                                    findChunks={isPhoneTitle ? undefined : findChunksOfWords}
                                    highlightClassName="super-search__item-text_bold"
                                    searchWords={props.searchValue.split(' ')}
                                    textToHighlight={isPhoneTitle ? item.name : formatNumberToPhone(item.msisdn)}
                                />
                            </SuperSearchListItemSecondary>
                        </SuperSearchListItem>
                    );
                })
            }
        </SuperSearchListGroupStyled>
    );
};

export default memo(SuperSearchItemMobiles);
